import angular from 'angular';
import { NsjRoutingType } from '@core/nsj-core';
import { MOVIMENTACOES_LOTE_LIMIT } from './models/movimentacoes.service.model';
import {
    IMovimentacaoWorkflowAcao,
    IMovimentacaoWorkflowEstado,
    IMovimentacaoWorkflowStatus
} from './models/movimentacoes-workflow.service.model';

export class MovimentacoesWorkflowService {
    static $inject = ['$rootScope', '$http', '$q', 'nsjRouting'];

    constructor(
        public $rootScope: angular.IRootScopeService,
        public $http: angular.IHttpService,
        public $q: angular.IQService,
        public nsjRouting: NsjRoutingType,
    ) { }

    public getWorkflowStatus(): angular.IPromise<IMovimentacaoWorkflowStatus> {
        return this.$q<IMovimentacaoWorkflowStatus>((resolve, reject) => {
            this.$http
                .get<IMovimentacaoWorkflowStatus>(this.nsjRouting.generate('movimentacoes_workflow', {}, true))
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_workflow_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_workflow_error', error);
                    reject(error);
                });
        });
    }

    public listWorkflowEstados(): angular.IPromise<IMovimentacaoWorkflowEstado[]> {
        return this.$q<IMovimentacaoWorkflowEstado[]>((resolve, reject) => {
            this.$http
                .get<IMovimentacaoWorkflowEstado[]>(this.nsjRouting.generate('movimentacoes_estados_workflow', {}, true))
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_estados_workflow_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_estados_workflow_error', error);
                    reject(error);
                });
        });
    }

    public listWorkflowAcoes(identifier: string): angular.IPromise<IMovimentacaoWorkflowAcao[]> {
        if (!this.isNonEmptyString(identifier)) {
            return this.rejectValidation('O identificador da movimentação deve ser informado.');
        }

        return this.$q<IMovimentacaoWorkflowAcao[]>((resolve, reject) => {
            this.$http
                .get<IMovimentacaoWorkflowAcao[]>(
                    this.nsjRouting.generate('movimentacoes_acoes_workflow', { id: identifier }, true)
                )
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_acoes_workflow_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_acoes_workflow_error', error);
                    reject(error);
                });
        });
    }

    public executarWorkflowAcao(identifier: string, codigo: string, motivo?: string | null): angular.IPromise<void> {
        if (!this.isNonEmptyString(identifier)) {
            return this.rejectValidation('O identificador da movimentação deve ser informado.');
        }

        if (!this.isNonEmptyString(codigo)) {
            return this.rejectValidation('O código da ação de workflow deve ser informado.');
        }

        if (typeof motivo === 'string' && motivo.trim().length > 500) {
            return this.rejectValidation('O motivo da ação de workflow deve ter no máximo 500 caracteres.');
        }

        const payload = this.buildWorkflowActionPayload(motivo);

        return this.$q<void>((resolve, reject) => {
            this.$http
                .post<void>(
                    this.nsjRouting.generate('movimentacoes_acao_workflow', { id: identifier, codigo }, true),
                    payload
                )
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_acao_workflow_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_acao_workflow_error', error);
                    reject(error);
                });
        });
    }

    public executarWorkflowAcaoLote(
        movimentacaoperiodo: string,
        codigo: string,
        movimentacoes: string[]
    ): angular.IPromise<void> {
        if (!this.isNonEmptyString(codigo)) {
            return this.rejectValidation('O código da ação de workflow deve ser informado.');
        }

        if (codigo === 'SOLICITAR_ESTORNO' || codigo === 'RECUSAR_ESTORNO') {
            return this.rejectValidation('Ação de workflow em lote com motivo não é suportada.');
        }

        return this.performWorkflowBatchAction(
            'movimentacoes_acao_workflow_lote',
            'movimentacoes_acao_workflow_lote_loaded',
            'movimentacoes_acao_workflow_lote_error',
            movimentacaoperiodo,
            codigo,
            movimentacoes
        );
    }

    private performWorkflowBatchAction(
        routeName: string,
        successEvent: string,
        errorEvent: string,
        movimentacaoperiodo: string,
        codigo: string,
        movimentacoes: string[]
    ): angular.IPromise<void> {
        if (!this.isNonEmptyString(movimentacaoperiodo)) {
            return this.rejectValidation('O parâmetro movimentacaoperiodo deve ser informado.');
        }

        if (!Array.isArray(movimentacoes)) {
            return this.rejectValidation('O lote de movimentações deve ser informado.');
        }

        if (movimentacoes.length > MOVIMENTACOES_LOTE_LIMIT) {
            return this.rejectValidation(`Informe no máximo ${MOVIMENTACOES_LOTE_LIMIT} movimentações.`);
        }

        if (movimentacoes.some((movimentacao) => !this.isNonEmptyString(movimentacao))) {
            return this.rejectValidation('Todas as movimentações devem ser informadas.');
        }

        return this.$q<void>((resolve, reject) => {
            this.$http
                .post<void>(
                    this.nsjRouting.generate(routeName, { movimentacaoperiodo, codigo }, true),
                    { movimentacoes }
                )
                .then((response) => {
                    this.$rootScope.$broadcast(successEvent, response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast(errorEvent, error);
                    reject(error);
                });
        });
    }

    private buildWorkflowActionPayload(motivo?: string | null): { motivo?: string } {
        const trimmedMotivo = typeof motivo === 'string' ? motivo.trim() : '';
        if (!trimmedMotivo) {
            return {};
        }

        if (trimmedMotivo.length > 500) {
            throw { message: 'O motivo da ação de workflow deve ter no máximo 500 caracteres.' };
        }

        return { motivo: trimmedMotivo };
    }

    private rejectValidation<T>(message: string): angular.IPromise<T> {
        return this.$q.reject({ message });
    }

    private isNonEmptyString(value: unknown): value is string {
        return typeof value === 'string' && value.trim().length > 0;
    }
}
